import React, { useEffect, useState } from "react";
import { ChevronDown, Check, Plus, TriangleAlert, Pill, ClipboardList, Activity, FlaskConical, FileText, Loader2, PanelRightClose } from "lucide-react";
import { fetchChartBundle, fetchPatientDocs, prettyType, itemInNote } from "@/lib/chartInsert";

import { INK, SUB, MUTE, HAIR } from "@/lib/tokens";
const BLUE = "#2F6FB3";

function Row({ it, staged, dim, onToggle, children }) {
  if (dim) {
    return (
      <div data-testid={`chartins-item-${it.key}`} title="Already in the note"
        className="w-full flex items-start gap-1.5 text-left rounded-[7px] px-1.5 py-1 opacity-55" style={{ background: "transparent" }}>
        <span className="mt-[1px] w-[17px] h-[17px] rounded-[5px] flex-none inline-flex items-center justify-center" style={{ border: `1.5px solid #C9C9C6`, color: SUB }}>
          <Check size={11} strokeWidth={3} style={{ color: "#9B9B97" }} />
        </span>
        <span className="min-w-0 flex-1">{children}<span className="ml-1 text-[9px] font-bold uppercase tracking-[.04em]" style={{ color: MUTE }}>· In note</span></span>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => onToggle(it)} data-testid={`chartins-item-${it.key}`}
      className="w-full flex items-start gap-1.5 text-left rounded-[7px] px-1.5 py-1 hover:bg-black/[.03]" style={{ background: staged ? "#EAF1FC" : "transparent" }}>
      <span className="mt-[1px] w-[17px] h-[17px] rounded-[5px] flex-none inline-flex items-center justify-center" style={{ border: `1.5px solid ${staged ? BLUE : "#C9C9C6"}`, background: staged ? BLUE : "transparent", color: "#fff" }}>
        {staged ? <Check size={11} strokeWidth={3} /> : <Plus size={11} style={{ color: SUB }} />}
      </span>
      {children}
    </button>
  );
}

function Section({ icon: Ic, title, k, items, open, onToggle, stagedKeys, onToggleItem, onToggleMany, renderItem, color, noteLow }) {
  const [showInNote, setShowInNote] = useState(false);
  const selectable = items.filter((it) => !itemInNote(it, noteLow));
  const inNote = items.filter((it) => itemInNote(it, noteLow));
  const allOn = selectable.length > 0 && selectable.every((it) => stagedKeys.has(it.key));
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: `1px solid ${HAIR}`, background: "#fff" }}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-1.5 px-2.5 py-2 hover:bg-black/[.02]" data-testid={`chartins-sec-${k}`}>
        <Ic size={13} style={{ color: color || SUB }} />
        <span className="text-[11.5px] font-bold uppercase tracking-[.04em]" style={{ color: INK }}>{title}</span>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums" style={{ background: "#F1F1F0", color: SUB }}>{selectable.length}</span>
        <span className="flex-1" />
        <ChevronDown size={13} style={{ color: MUTE, transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s" }} />
      </button>
      {open ? (
        <div className="px-2 pb-2 space-y-0.5" style={{ borderTop: `1px solid ${HAIR}` }}>
          {items.length === 0 ? (
            <div className="text-[11.5px] px-1.5 py-1.5" style={{ color: MUTE }}>None on chart.</div>
          ) : (
            <>
              {selectable.length >= 2 ? (
                <button type="button" onClick={() => onToggleMany(selectable, !allOn)} className="text-[10.5px] font-bold mt-1 ml-1.5" style={{ color: BLUE }} data-testid={`chartins-addall-${k}`}>{allOn ? "Clear all" : "Select all"}</button>
              ) : null}
              {selectable.length === 0 ? (
                <div className="text-[11.5px] px-1.5 py-1.5" style={{ color: MUTE }} data-testid={`chartins-allinnote-${k}`}>{inNote.length === 1 ? "Already in the note" : `All ${inNote.length} already in the note`}</div>
              ) : selectable.map((it) => (
                <Row key={it.key} it={it} staged={stagedKeys.has(it.key)} dim={false} onToggle={onToggleItem}>
                  {renderItem ? renderItem(it) : <span className="text-[12px] leading-snug" style={{ color: SUB }}>{it.text}</span>}
                </Row>
              ))}
              {inNote.length ? (
                <>
                  <button type="button" onClick={() => setShowInNote((v) => !v)} className="text-[12px] font-semibold mt-1 ml-1.5 hover:text-[#141412]" style={{ color: SUB }} data-testid={`chartins-showinnote-${k}`}>{showInNote ? "Hide" : "Show"} {inNote.length} already in the note</button>
                  {showInNote ? (
                    <div className="space-y-0.5 pt-1 mt-1" style={{ borderTop: `1px dashed ${HAIR}` }}>
                      {inNote.map((it) => (
                        <Row key={it.key} it={it} staged={false} dim onToggle={onToggleItem}>
                          {renderItem ? renderItem(it) : <span className="text-[12px] leading-snug" style={{ color: SUB }}>{it.text}</span>}
                        </Row>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

const MedItem = (it) => (
  <span className="min-w-0 flex-1">
    <span className="text-[12px] font-semibold" style={{ color: INK }}>{it.name}{it.dosage ? ` ${it.dosage}` : ""}</span>
    {it.sig ? <span className="text-[12px]" style={{ color: SUB }}> — {it.sig}</span> : null}
    <span className="block text-[10.5px]" style={{ color: MUTE }}>{it.start || "?"} → {it.end || "?"}</span>
  </span>
);
const ProblemItem = (it) => (
  <span className="min-w-0 flex-1">
    <span className="text-[12px]" style={{ color: SUB }}>{it.code ? <b style={{ color: INK }}>{it.code} </b> : null}{it.desc}</span>
    {(it.onset || it.last) ? <span className="block text-[10.5px]" style={{ color: MUTE }}>{[it.onset ? `onset ${it.onset}` : "", it.last ? `last eval ${it.last}` : ""].filter(Boolean).join(" · ")}</span> : null}
  </span>
);
const DocItem = (it) => (
  <span className="min-w-0 flex-1">
    <span className="block text-[12px] font-semibold truncate" style={{ color: INK }}>{it.name}</span>
    <span className="block text-[10.5px]" style={{ color: MUTE }}>{prettyType(it.type)}{it.date ? ` · ${it.date}` : ""}</span>
  </span>
);

export function ChartInsertPanel({ note, stagedKeys, onToggleItem, onToggleMany, onCollapse, noteText, variant }) {
  const uuid = note?.chart_uuid || note?.chart_id || "";
  const clientId = note?.client_id || "";
  const mrn = note?.mrn || "";
  const [b, setB] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({ allergies: true, meds: true, probChronic: true, probAcute: true, vitals: false, labs: false, documents: false });
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const [bundle, d] = await Promise.all([uuid ? fetchChartBundle(uuid) : null, fetchPatientDocs(clientId, mrn, uuid)]);
        if (on) { setB(bundle); setDocs(d || []); }
      } catch { /* leave empty */ } finally { if (on) setLoading(false); }
    })();
    return () => { on = false; };
  }, [uuid, clientId, mrn]);

  const d = b || {};
  const noteLow = String(noteText || "").toLowerCase();
  const common = { stagedKeys, onToggleItem, onToggleMany, noteLow };
  const card = variant === "card";
  return (
    <div className={card ? "w-full flex-1 min-h-0 flex flex-col rounded-[11px] overflow-hidden" : "w-full md:w-[360px] flex-none flex flex-col min-h-0"} data-testid="chart-insert-panel" style={card ? { border: `1px solid ${HAIR}`, background: "#fff" } : { borderLeft: `1px solid ${HAIR}` }}>
      <div className="px-3 pt-2.5 pb-2 flex items-center gap-1.5 flex-none" style={{ borderBottom: `1px solid ${HAIR}` }}>
        <ClipboardList size={13} style={{ color: SUB }} />
        <span className="text-[11px] font-bold uppercase tracking-[.05em]" style={{ color: INK }}>Pull from chart</span>
        <span className="text-[10.5px]" style={{ color: MUTE }}>· click to add</span>
        <span className="flex-1" />
        {onCollapse ? <button type="button" onClick={onCollapse} title="Hide panel" data-testid="chartins-collapse" className="w-[24px] h-[24px] rounded-[6px] inline-flex items-center justify-center hover:bg-black/[.05]" style={{ color: SUB }}><PanelRightClose size={15} /></button> : null}
      </div>
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-[12px]" style={{ color: MUTE }}><Loader2 size={14} className="animate-spin mr-2" /> Loading chart…</div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto p-2.5 space-y-2">
          <Section icon={TriangleAlert} color="#C4302A" title="Allergies" k="allergies" items={d.allergies || []} open={open.allergies} onToggle={() => toggle("allergies")} {...common} />
          <Section icon={Pill} title="Medications" k="meds" items={d.meds || []} open={open.meds} onToggle={() => toggle("meds")} renderItem={MedItem} {...common} />
          <Section icon={ClipboardList} title="Problems · chronic" k="probChronic" items={d.problemsChronic || []} open={open.probChronic} onToggle={() => toggle("probChronic")} renderItem={ProblemItem} {...common} />
          <Section icon={ClipboardList} title="Problems · acute" k="probAcute" items={d.problemsAcute || []} open={open.probAcute} onToggle={() => toggle("probAcute")} renderItem={ProblemItem} {...common} />
          <Section icon={Activity} title="Vitals" k="vitals" items={d.vitals || []} open={open.vitals} onToggle={() => toggle("vitals")} {...common} />
          <Section icon={FlaskConical} title="Labs & imaging" k="labs" items={d.labs || []} open={open.labs} onToggle={() => toggle("labs")} {...common} />
          <Section icon={FileText} title="Documents" k="documents" items={docs} open={open.documents} onToggle={() => toggle("documents")} renderItem={DocItem} {...common} />
        </div>
      )}
    </div>
  );
}
